import { Input, Select, UnstyledButton, useMantineTheme } from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { ErrorBoundary } from '@sentry/react'
import { LuresMultiSelect } from 'App/ServiceCenter/Shared/LuresMultiSelect'
import { NodeInfoHeader } from 'App/ServiceCenter/Shared/NodeInfoHeader/NodeInfoHeader'
import type { NodeConfigUpdateMeta } from 'App/ServiceCenter/utils/api/serviceCenterNodeConfigSet'
import { updateNodeConfig } from 'App/ServiceCenter/utils/updateNodeConfig'
import { AboveAllModal } from 'components/AboveAllModalOverlay/AboveAllModalOverlay'
import { FIXED_CHILDREN_MODAL_TRANSITION } from 'components/AboveAllModalOverlay/fixedChildrenModalTransition'
import { IconHelpCircle } from 'components/icons/IconHelpCircle'
import { WideHeader } from 'components/ModalDrawer/WideHeader/WideHeader'
import { translate } from 'i18n/i18n'
import _ from 'lodash'
import React, { useMemo, useState } from 'react'
import { SharedSettings } from 'settings/SharedSettings'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { useSelectClickToggle } from 'utils/useSelectClickToggle'
import { checkAuthorization } from '../../../../utils/checkAuthorization'
import { serviceCenterStore } from '../../store/serviceCenterStore'
import type { TActiveNode } from '../../types'
import { ConsumableType } from '../../types'
import { Footer } from '../Footer/Footer'

const MODAL_ID = 'trap-configuration'

type FormValues = {
  lureIds: number[]
  insectId: number | null
}

type Props = {
  showHeader?: boolean
  onClose: () => void
  onSubmit?: () => void
  node?: TActiveNode
}

export const openTrapConfigurationModal = (node?: TActiveNode) => {
  AboveAllModal.open({
    modalId: MODAL_ID,
    fullScreen: true,
    withCloseButton: false,
    padding: 0,
    transitionProps: FIXED_CHILDREN_MODAL_TRANSITION,
    children: (
      <ErrorBoundary>
        <TrapConfiguration node={node} showHeader={true} onClose={() => AboveAllModal.close(MODAL_ID)} />
      </ErrorBoundary>
    ),
    styles: {
      content: {
        marginLeft: 'env(safe-area-inset-left)',
        marginRight: 'env(safe-area-inset-right)',
        boxShadow: 'none',
      },
    },
  })
}

export const TrapConfiguration: React.FC<Props> = ({ onClose, onSubmit, showHeader = false, node }) => {
  const theme = useMantineTheme()
  const selectClickToggle = useSelectClickToggle()

  const selectedNode = node
    ? node
    : serviceCenterStore.useSelector(serviceCenterStore.selectors.getSelectedEquipmentNode)

  const [isSaving, setIsSaving] = useState(false)
  const allInsects = fieldAssetStore.useSelector((s) => s.insects)
  const { propertyId } = selectedNode as TActiveNode

  const canSetInsect = useMemo(
    () =>
      checkAuthorization({
        permission: 'EDIT_SSC_NODE_INSECT_SET',
        entity: propertyId,
      }),
    [propertyId],
  )

  const initialValues = useMemo(() => {
    let lureIds: number[] = []

    if (!_.isEmpty((selectedNode as TActiveNode)?.trap?.lures.active)) {
      lureIds = Object.values((selectedNode as TActiveNode)?.trap?.lures.active || {}).map(
        (lure) => lure.lureId,
      )
    } else if (!_.isEmpty((selectedNode as TActiveNode)?.trap?.lures.planned)) {
      lureIds = Object.values((selectedNode as TActiveNode)?.trap?.lures.planned || {}).map(
        (lure) => lure.lureId,
      )
    }

    return {
      lureIds,
      insectId: (selectedNode as TActiveNode)?.trap?.targetInsectId || null,
    }
  }, [selectedNode])

  const form = useForm<FormValues>({
    initialValues,
    validate: {
      insectId: (value) =>
        !canSetInsect || value ? null : translate.phrases.placeholder('Please select a target pest'),
    },
  })

  if (!allInsects || !selectedNode) return null

  const insectOptions = _.sortBy(
    Object.values(allInsects)
      .filter((insect) => insect.enabled)
      .map((insect) => ({ label: insect.name, value: String(insect.insectId) })),
    'label',
  )

  const handleSubmit = async () => {
    if (!propertyId) return

    const validation = form.validate()

    if (validation.hasErrors) {
      return
    }

    setIsSaving(true)

    const {
      values: { lureIds, insectId },
    } = form

    try {
      const payload = {
        nodeIdentifier: (selectedNode as TActiveNode).nodeIdentifier,
        propertyId,
        nodeType: (selectedNode as TActiveNode).nodeType,
        trap: {
          lureIds,
          consumableTypes: [ConsumableType.LURE],
          consumableOperationType: lureIds.length ? 'SET' : 'REMOVE',
        },
      } as NodeConfigUpdateMeta

      if (canSetInsect && payload.trap) {
        payload.trap.targetInsectId = insectId || null
      }

      await updateNodeConfig(payload)

      onSubmit && onSubmit()

      onClose()
    } catch (error) {
      setIsSaving(false)

      let errorMessage: string = error as string

      if (error instanceof Error) {
        errorMessage = error.message
      }

      notifications.show({
        title: translate.phrases.placeholder('Error'),
        message: errorMessage,
        color: 'red',
      })
    }

    setIsSaving(false)
  }

  return (
    <>
      {showHeader && (
        <WideHeader
          title={translate.phrases.placeholder('Configure Trap')}
          onClose={() => onClose()}
          isSecondaryModal={true}
          rightIconButton={
            <UnstyledButton onClick={() => window.open(SharedSettings.SSC_INSTALLATION_GUIDES_URL, '_blank')}>
              <span css={{ fontSize: 20, color: theme.colors.midnight[0] }}>
                <IconHelpCircle />
              </span>
            </UnstyledButton>
          }
        />
      )}

      <div css={{ padding: 20 }}>
        {!showHeader && (
          <div css={{ marginBottom: 20 }}>
            <NodeInfoHeader node={selectedNode as TActiveNode} />
          </div>
        )}

        <Select
          {...selectClickToggle}
          css={{ marginBottom: '16px' }}
          placeholder={translate.phrases.placeholder('Target Pest')}
          data={insectOptions}
          {...form.getInputProps('insectId')}
          value={form.values.insectId === null ? null : String(form.values.insectId)}
          onChange={(value) => {
            form.setFieldValue('insectId', value === null ? null : Number(value))

            // when insect has changed, clear lures
            form.setFieldValue('lureIds', [])
          }}
          required={canSetInsect}
          disabled={!canSetInsect}
          label={translate.phrases.placeholder('Target Pest')}
          error={form.errors.insectId}
          searchable
          styles={SharedSettings.MANTINE_SELECT_RIGHT_ICON_CHANGER}
        />

        <Input.Wrapper
          id="lureIds"
          css={{ marginTop: 20 }}
          label={translate.phrases.placeholder('Lure Type')}
          error={form.errors.lureIds}
        >
          <LuresMultiSelect
            targetInsectId={form.values?.insectId}
            propertyId={propertyId}
            {...form.getInputProps('lureIds')}
          />
        </Input.Wrapper>

        <Footer
          onNext={handleSubmit}
          nextButtonLabel={translate.phrases.placeholder('Configure')}
          disableNextButton={isSaving}
          loading={isSaving}
          onPrevious={() => onClose()}
        />
      </div>
    </>
  )
}
